import Script from 'next/script';
import React from 'react';
import { DEFAULT_LOCALE } from '@/constants/locales';
import TrustpilotLogo from '@/public/icons/trustpilot-logo.svg';
import styles from './TrustpilotWidget.module.scss';

const TRUSTPILOT_SCRIPT_SRC =
	'https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js';

/** "Micro Combo" TrustBox — stars, rating word, review count and logo in one row. */
const MICRO_COMBO_TEMPLATE_ID = '5419b6ffb0d04a076446a9af';
const BUSINESS_UNIT_ID = '68791fd904686208777c90d6';
const WIDGET_TOKEN = 'f600bb07-8161-4a15-a910-70f22fd8524c';
const REVIEW_URL = 'https://www.trustpilot.com/review/liftapp.ai';

// Trustpilot serves only a subset of the app's locales; everything else falls
// back to English rather than rendering an empty box
const TRUSTPILOT_LOCALES: Record<string, string> = {
	en: 'en-US',
	es: 'es-ES',
	pt: 'pt-PT',
	de: 'de-DE',
	it: 'it-IT',
	fr: 'fr-FR',
	pl: 'pl-PL',
	tr: 'tr-TR',
	nl: 'nl-NL',
};

const FALLBACK_TRUSTPILOT_LOCALE =
	TRUSTPILOT_LOCALES[DEFAULT_LOCALE] ?? 'en-US';

declare global {
	interface Window {
		Trustpilot?: {
			loadFromElement: (element: HTMLElement, rerender?: boolean) => void;
		};
	}
}

// Types
interface IProps {
	/** App locale (`en`, `de`, …); mapped to the closest Trustpilot locale. */
	locale?: string;
	/** Applied to the link, which is the whole clickable surface. */
	className?: string;
	/** Reserve the widget's height so the card doesn't jump once it renders. */
	height?: string;
	width?: string;
	/** `dark` for a dark backdrop: white text and logo */
	theme?: 'light' | 'dark';
}

export const TrustpilotWidget: React.FC<IProps> = ({
	locale,
	className,
	height = '20px',
	width = '100%',
	theme = 'light',
}) => {
	const ref = React.useRef<HTMLDivElement>(null);

	const render = React.useCallback(() => {
		if (!ref.current || !window.Trustpilot) return;

		window.Trustpilot.loadFromElement(ref.current, true);
	}, []);

	// The bootstrap script only scans the DOM once, on load. On client-side
	// navigation it is already cached and fired, so the element has to ask for
	// itself — `onLoad` alone would never run again.
	React.useEffect(() => {
		render();
	}, [render]);

	return (
		<>
			<Script
				src={TRUSTPILOT_SCRIPT_SRC}
				strategy='lazyOnload'
				onLoad={render}
			/>

			{/*
				Trustpilot's own snippet puts the fallback link inside the widget
				element; it lives outside here so the whole surface — the card the
				widget sits in, not just the stars — opens the review page. Nesting
				the two anchors is not an option: the HTML parser would split them
				apart and the script would lose the element it renders into.
			*/}
			<a
				className={`${styles['link']}${className ? ` ${className}` : ''}`}
				href={REVIEW_URL}
				target='_blank'
				rel='noopener noreferrer'
				aria-label='Trustpilot'
			>
				<div
					ref={ref}
					className={`trustpilot-widget ${styles['widget']}`}
					data-locale={
						(locale && TRUSTPILOT_LOCALES[locale]) ?? FALLBACK_TRUSTPILOT_LOCALE
					}
					data-template-id={MICRO_COMBO_TEMPLATE_ID}
					data-businessunit-id={BUSINESS_UNIT_ID}
					data-style-height={height}
					data-style-width={width}
					data-theme={theme}
					data-token={WIDGET_TOKEN}
				>
					Trustpilot
				</div>

				{/*
					Narrow enough and the Micro Combo drops its own review count and
					logo, leaving a row of stars that says nothing about who scored
					them — so the brand is put back under it by hand. It is hidden
					again at the widths where the TrustBox carries its own logo.
				*/}
				<TrustpilotLogo
					className={`${styles['logo']}${
						theme === 'dark' ? ` ${styles['logo_dark']}` : ''
					}`}
					// svgr strips the viewBox off this file, which leaves the artwork
					// pinned to its intrinsic 97×24 and clipped by any other size
					viewBox='0 0 97 24'
					aria-hidden='true'
					focusable='false'
				/>
			</a>
		</>
	);
};
